import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Bell, Phone, Mail, MessageCircle, Send, AlertTriangle } from "lucide-react";
import Layout from "../components/layout/Layout";
import AuthGuard from "../components/AuthGuard";
import EmptyState from "../components/EmptyState";
import { api } from "../api/client";

const EMPTY = {
    caregiver_name: "",
    caregiver_phone: "",
    caregiver_email: "",
    relationship: "spouse",
    escalation_priority: 1,
    channels: { sms: true, whatsapp: false, email: true, in_app: true },
};

const RELATIONSHIPS = ["spouse", "parent", "sibling", "child", "friend", "professional caregiver", "other"];

const CaregiverForm = ({ initial, onSubmit, onCancel, submitting }) => {
    const [form, setForm] = useState(() => ({
        ...EMPTY,
        ...(initial || {}),
        channels: { ...EMPTY.channels, ...(initial?.channels || {}) },
    }));

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    const onChannel = (k) => setForm((f) => ({ ...f, channels: { ...f.channels, [k]: !f.channels[k] } }));

    const submit = (e) => {
        e.preventDefault();
        const payload = {
            caregiver_name: form.caregiver_name.trim(),
            caregiver_phone: form.caregiver_phone.trim(),
            caregiver_email: form.caregiver_email.trim() || null,
            relationship: form.relationship,
            channels: form.channels,
            escalation_priority: Number(form.escalation_priority) || 1,
        };
        onSubmit(payload);
    };

    return (
        <form onSubmit={submit} data-testid="caregiver-form" className="space-y-4 rounded-3xl border border-brand-100 bg-brand-50/40 p-6 dark:border-brand-400/20 dark:bg-brand-500/5">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block md:col-span-2">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Caregiver name</span>
                    <input name="caregiver_name" required value={form.caregiver_name} onChange={onChange} placeholder="e.g. Priya Sharma" className="input-clean mt-1" data-testid="cg-name" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Phone</span>
                    <input name="caregiver_phone" required value={form.caregiver_phone} onChange={onChange} placeholder="+91 98xxxxxxxx" className="input-clean mt-1" data-testid="cg-phone" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Email (optional)</span>
                    <input type="email" name="caregiver_email" value={form.caregiver_email} onChange={onChange} placeholder="priya@example.com" className="input-clean mt-1" data-testid="cg-email" />
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Relationship</span>
                    <select name="relationship" value={form.relationship} onChange={onChange} className="input-clean mt-1" data-testid="cg-rel">
                        {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </label>
                <label className="block">
                    <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Escalation priority (1 = first)</span>
                    <input type="number" name="escalation_priority" min="1" max="5" value={form.escalation_priority} onChange={onChange} className="input-clean mt-1" data-testid="cg-priority" />
                </label>
            </div>
            <div>
                <p className="text-xs font-medium text-zinc-600 dark:text-zinc-300">Notification channels</p>
                <div className="mt-2 flex flex-wrap gap-2">
                    {[
                        { k: "sms", label: "SMS", icon: MessageCircle },
                        { k: "whatsapp", label: "WhatsApp", icon: MessageCircle },
                        { k: "email", label: "Email", icon: Mail },
                        { k: "in_app", label: "In-app", icon: Bell },
                    ].map(({ k, label, icon: Icon }) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => onChannel(k)}
                            data-testid={`cg-channel-${k}`}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                                form.channels[k]
                                    ? "border-brand-500 bg-brand-700 text-white"
                                    : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 dark:border-white/10 dark:bg-white/[0.03] dark:text-zinc-300"
                            }`}
                        >
                            <Icon className="h-3.5 w-3.5" /> {label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onCancel} className="rounded-full border border-zinc-200 px-4 py-2 text-sm hover:bg-zinc-50 dark:border-white/10 dark:hover:bg-white/5" data-testid="cg-cancel">Cancel</button>
                <button type="submit" disabled={submitting} className="btn-brand !py-2 !px-5 !text-sm" data-testid="cg-save">
                    {submitting ? "Saving..." : "Save caregiver"}
                </button>
            </div>
        </form>
    );
};

const CHANNEL_ICONS = { sms: MessageCircle, whatsapp: MessageCircle, email: Mail, in_app: Bell };

const CaregiverCard = ({ c, onEdit, onDelete, onTest, testing }) => (
    <div data-testid={`caregiver-card-${c.id}`} className="rounded-2xl border border-zinc-100 bg-white p-5 transition hover:border-brand-200 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-brand-400/40">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{c.caregiver_name}</p>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">{c.relationship || "—"}</p>
                <p className="mt-2 inline-flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-300">
                    <Phone className="h-3 w-3" /> {c.caregiver_phone}
                </p>
                {c.caregiver_email && (
                    <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-300">
                        <Mail className="h-3 w-3" /> {c.caregiver_email}
                    </p>
                )}
            </div>
            <div className="flex items-center gap-1">
                <button onClick={() => onEdit(c)} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-50 hover:text-brand-700 dark:hover:bg-white/5" aria-label="Edit" data-testid={`cg-edit-${c.id}`}>
                    <Edit3 className="h-4 w-4" />
                </button>
                <button onClick={() => onDelete(c)} className="rounded-full p-2 text-zinc-500 hover:bg-zinc-50 hover:text-rose-500 dark:hover:bg-white/5" aria-label="Delete" data-testid={`cg-delete-${c.id}`}>
                    <Trash2 className="h-4 w-4" />
                </button>
            </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-zinc-100 pt-3 dark:border-white/10">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
                Priority {c.escalation_priority}
            </span>
            {Object.entries(c.channels || {}).filter(([, on]) => on).map(([k]) => {
                const Icon = CHANNEL_ICONS[k] || Bell;
                return (
                    <span key={k} className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                        <Icon className="h-3 w-3" /> {k.replace("_", "-")}
                    </span>
                );
            })}
        </div>
        <button
            onClick={() => onTest(c)}
            disabled={testing}
            data-testid={`cg-test-${c.id}`}
            className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full border border-brand-200 px-3 py-1.5 text-xs font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50 dark:border-brand-400/30 dark:text-brand-300 dark:hover:bg-brand-500/10"
        >
            <Send className="h-3 w-3" />
            {testing ? "Sending..." : "Send test alert"}
        </button>
    </div>
);

const CaregiversContent = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [testingId, setTestingId] = useState(null);

    const load = async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/caregivers");
            setItems(data?.items || []);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const onCreate = async (payload) => {
        try {
            setSubmitting(true);
            await api.post("/caregivers", payload);
            toast.success("Caregiver added");
            setAdding(false);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save");
        } finally { setSubmitting(false); }
    };

    const onUpdate = async (payload) => {
        try {
            setSubmitting(true);
            await api.patch(`/caregivers/${editing.id}`, payload);
            toast.success("Caregiver updated");
            setEditing(null);
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally { setSubmitting(false); }
    };

    const onDelete = async (c) => {
        if (!window.confirm(`Remove ${c.caregiver_name} as caregiver?`)) return;
        try {
            await api.delete(`/caregivers/${c.id}`);
            toast.success("Removed");
            await load();
        } catch { toast.error("Delete failed"); }
    };

    const onTest = async (c) => {
        try {
            setTestingId(c.id);
            const { data } = await api.post(`/caregivers/${c.id}/test-notify`);
            toast.success(`Test sent via: ${(data.channels_attempted || []).join(", ") || "—"}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not send");
        } finally { setTestingId(null); }
    };

    return (
        <div data-testid="caregivers-page">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="label-uppercase">Caregivers</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">Trusted contacts for emergencies</h1>
                    <p className="mt-2 max-w-xl text-sm text-zinc-600 dark:text-zinc-400">
                        We'll alert your caregivers (in escalation order) if you miss a critical medication or log a dangerous reading.
                    </p>
                </div>
                {!adding && !editing && (
                    <button onClick={() => setAdding(true)} className="btn-brand !py-2 !px-4 !text-sm" data-testid="caregiver-add-btn">
                        <Plus className="h-4 w-4" /> Add caregiver
                    </button>
                )}
            </div>

            {(adding || editing) && (
                <div className="mb-6">
                    <CaregiverForm
                        initial={editing}
                        onSubmit={editing ? onUpdate : onCreate}
                        onCancel={() => { setAdding(false); setEditing(null); }}
                        submitting={submitting}
                    />
                </div>
            )}

            <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/5 dark:text-amber-200">
                <div className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>
                        SMS, WhatsApp and email channels are <strong>simulated for now</strong>. In-app delivery is real if your caregiver also uses PILLS.
                        Live SMS/Email integrations are scheduled for a later phase.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[1, 2, 3].map((i) => <div key={i} className="h-44 skeleton rounded-2xl" />)}
                </div>
            ) : items.length === 0 ? (
                <EmptyState
                    title="No caregivers added"
                    description="Add a family member or friend who should be alerted if you miss a critical dose."
                    action={<button onClick={() => setAdding(true)} className="btn-brand">Add first caregiver</button>}
                />
            ) : (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="caregivers-grid">
                    {items.map((c) => (
                        <CaregiverCard key={c.id} c={c} onEdit={(x) => { setEditing(x); setAdding(false); }} onDelete={onDelete} onTest={onTest} testing={testingId === c.id} />
                    ))}
                </div>
            )}
        </div>
    );
};

const Caregivers = () => (
    <Layout>
        <section className="mx-auto max-w-7xl px-6 py-10 lg:px-12">
            <AuthGuard>
                <CaregiversContent />
            </AuthGuard>
        </section>
    </Layout>
);

export default Caregivers;
